import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/types/database';
import {
  LayoutDashboard,
  Laptop,
  Package,
  ShoppingBag,
  Receipt,
  RotateCcw,
  Users,
  Building2,
  Wrench,
  ShieldCheck,
  DollarSign,
  BarChart3,
  History,
  Settings,
  ScanLine,
  ExternalLink,
  ClipboardList,
  Store,
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  roles: UserRole[];
}

const navItems: NavItem[] = [
  { name: 'Dashboard', path: '/app', icon: LayoutDashboard, roles: ['owner', 'manager', 'staff'] },
  { name: 'Laptops', path: '/app/inventory/laptops', icon: Laptop, roles: ['owner', 'manager', 'staff'] },
  { name: 'Accessories', path: '/app/inventory/accessories', icon: Package, roles: ['owner', 'manager', 'staff'] },
  { name: 'Public Catalogue', path: '/app/catalog', icon: Store, roles: ['owner', 'manager'] },
  { name: 'Sales & Invoices', path: '/app/sales', icon: Receipt, roles: ['owner', 'manager', 'staff'] },
  { name: 'Repairs Kanban', path: '/app/repairs', icon: Wrench, roles: ['owner', 'manager', 'staff'] },
  { name: 'Warranties', path: '/app/warranties', icon: ShieldCheck, roles: ['owner', 'manager', 'staff'] },
  { name: 'Customers', path: '/app/customers', icon: Users, roles: ['owner', 'manager', 'staff'] },
  { name: 'Purchases', path: '/app/purchases', icon: ShoppingBag, roles: ['owner', 'manager'] },
  { name: 'Returns', path: '/app/returns', icon: RotateCcw, roles: ['owner', 'manager'] },
  { name: 'Suppliers', path: '/app/suppliers', icon: Building2, roles: ['owner', 'manager'] },
  { name: 'Expenses', path: '/app/expenses', icon: DollarSign, roles: ['owner', 'manager'] },
  { name: 'Reports', path: '/app/reports', icon: BarChart3, roles: ['owner', 'manager'] },
  { name: 'Stocktake', path: '/app/stocktake', icon: ClipboardList, roles: ['owner', 'manager'] },
  { name: 'Audit Log', path: '/app/audit', icon: History, roles: ['owner'] },
  { name: 'Scanner', path: '/app/scanner', icon: ScanLine, roles: ['owner', 'manager', 'staff'] },
  { name: 'Settings', path: '/app/settings', icon: Settings, roles: ['owner', 'manager'] },
];

export const Sidebar: React.FC = () => {
  const { role } = useAuth();

  const filteredItems = navItems.filter((item) => role && item.roles.includes(role));

  return (
    <aside className="w-64 bg-brand-black text-white flex flex-col h-screen sticky top-0 border-r border-neutral-800 shrink-0">
      {/* Brand Header */}
      <div className="p-4 border-b border-neutral-800 flex items-center space-x-3">
        <img
          src="/reference/al-kuwait-tech-logo.png"
          alt="Al Kuwait Tech Logo"
          className="h-10 w-auto object-contain bg-white rounded p-1"
        />
        <div>
          <h1 className="font-bold text-sm leading-tight text-white tracking-wide">
            AL KUWAIT TECH
          </h1>
          <p className="text-[11px] text-emerald-400 font-medium">Retail & Repair PWA</p>
        </div>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
          Main Menu
        </div>
        {filteredItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/app'}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-green text-white shadow-sm'
                    : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{item.name}</span>
            </NavLink>
          );
        })}
      </div>

      {/* Public Catalog Link */}
      <div className="p-3 border-t border-neutral-800">
        <a
          href="/shop"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between px-3 py-2 text-xs font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
        >
          <span>Public Catalogue</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Role Indicator Footer */}
      <div className="p-3 bg-neutral-900 border-t border-neutral-800 flex items-center justify-between text-xs">
        <span className="text-neutral-400">Active Role</span>
        <span className="capitalize font-semibold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-800">
          {role || 'staff'}
        </span>
      </div>
    </aside>
  );
};
